// Deeplink Test page — flow catalogue
// Reference: TECHNICAL DESIGN DOCUMENT — Deeplink (Universal Link) Improvement v1.0
//            §1.2 Supported Destinations, §8 Universal Link Examples
//            + QA "Deeplink_TestCase.xlsx" expected-result column
//
// Purpose: give QA one tappable link per deeplink journey so that opening this
// page on a device (or scanning) and tapping a flow reproduces the real user
// journey on UAT.
//
// Scope: UAT ONLY (per request). Every link points at the UAT web-landing route
// which resolves platform + app-install and hands off to the cpamapp:// app
// handler (or the store / QR fallback). Change UAT_LANDING_BASE below to retarget.

// UAT Custom Redirect Page (Screen D/E). doc §1.3 — UAT web app host.
export const UAT_LANDING_BASE =
  "https://app.staging.principal.th/deeplink/landing";

// destination enum accepted by the landing page (kept in sync with
// src/containers/DeeplinkLanding/constants.js -> DESTINATIONS).
export const DESTINATIONS = {
  FUND_TAB: "fund_tab",
  FUND_INFO: "fund_info",
  ARTICLE: "article",
  OPEN_MF: "open_mf",
  OPEN_PVD: "open_pvd",
  SOFT_ONBOARD: "soft_onboard",
  FORGOT_CREDENTIALS: "forgot_credentials",
};

// Build a UAT landing URL. `extra` carries the content identifier for the
// public content flows (fund code / article id). The landing page currently
// reads `destination` (+ lang/ref_id); the extra keys are forwarded so the
// link already reflects the real journey once the landing threads them through.
export const buildUatLink = (destination, extra = {}) => {
  const params = new URLSearchParams({ destination, lang: "th", ...extra });
  return `${UAT_LANDING_BASE}?${params.toString()}`;
};

// Sample content identifiers used in the public flows (from the QA test cases).
//
// ARTICLE_ID must be the `article.id` exactly as returned by the feed API
// (GET /feeds/api/v1/public/feeds/article?fields=All) — the app resolves the
// deeplink by scanning that list for `String(article.id) === String(feedId)`.
// A CMS content code such as "Principal_Tactical_Call_20042026" never matches any
// id, and the response carries no field holding such a code, so the app silently
// falls back to the home feed and the article never opens.
//
// Caveat: that endpoint returns only the 100 most recent articles, so a sample id
// stops resolving once the article drops out of that window — if this link starts
// landing on the feed again, pick a current id from the endpoint above. Note the
// ids are not all numeric (the list also carries "ADD_<timestamp>" ids), so copy
// the value verbatim rather than assuming a number.
//
// 2026-08-26: refreshed 4139 -> 1026437. The caveat above fired — the VNEQ article
// is still published but its id changed on the backend and 4139 is no longer in
// the 100-item window, so the old link had started landing on the feed. 1026437
// verified live the same day (iOS sim, UAT build against staging, guest + logged
// out, warm and cold launch): the article page renders in full.
export const SAMPLE = {
  FUND_CODE: "MABALANCED-D",
  ARTICLE_ID: "1026437",
};

// Two groups per the doc: Public (any user) and Private (needs an authed session).
export const FLOW_GROUPS = [
  {
    key: "public",
    title: "Public — เปิดได้ทุกผู้ใช้ (ไม่ต้องล็อกอิน)",
    flows: [
      {
        key: DESTINATIONS.FUND_TAB,
        nameTh: "หน้ารวมกองทุน (Fund Tab)",
        nameEn: "Fund Tab",
        scheme: "cpamapp://fund-tab",
        url: buildUatLink(DESTINATIONS.FUND_TAB),
        expected:
          "เปิดแอปแล้วไปหน้า Fund Tab ทันที เห็นรายการกองทุน",
      },
      {
        key: DESTINATIONS.FUND_INFO,
        nameTh: "ข้อมูลกองทุน (Fund Information)",
        nameEn: "Fund Information",
        scheme: `cpamapp://fund-info?fundCode=${SAMPLE.FUND_CODE}`,
        url: buildUatLink(DESTINATIONS.FUND_INFO, { fundCode: SAMPLE.FUND_CODE }),
        expected: `เปิดหน้า Fund Info ของ ${SAMPLE.FUND_CODE} (NAV + สัดส่วนการลงทุน)`,
      },
      {
        key: DESTINATIONS.ARTICLE,
        nameTh: "บทความ (Article)",
        nameEn: "Article",
        // The app parses the article id from `?feedId=` (linking config registers
        // path `feed` with `article` as an alias) — a path segment such as
        // cpamapp://article/{id} does not match and falls through to the default
        // screen.
        scheme: `cpamapp://article?feedId=${SAMPLE.ARTICLE_ID}`,
        url: buildUatLink(DESTINATIONS.ARTICLE, { feedId: SAMPLE.ARTICLE_ID }),
        expected:
          `เปิดหน้ารายละเอียดบทความที่ระบุ (id ${SAMPLE.ARTICLE_ID} = ตลาดหุ้นเวียดนามฯ PRINCIPAL VNEQ)`,
      },
    ],
  },
  {
    key: "private",
    title: "Private — ต้องมี session (PIN / Biometrics / Login)",
    flows: [
      {
        key: DESTINATIONS.OPEN_MF,
        nameTh: "เปิดบัญชีกองทุนรวม (Open MF)",
        nameEn: "Open Mutual Fund",
        scheme: "cpamapp://open-mf",
        url: buildUatLink(DESTINATIONS.OPEN_MF),
        expected:
          "ยืนยัน session แล้วเข้า flow เปิดบัญชี MF (ผู้ที่มี MF อยู่แล้วจะข้ามไปหน้า Feed)",
      },
      {
        key: DESTINATIONS.OPEN_PVD,
        nameTh: "เปิดบัญชีกองทุนสำรองเลี้ยงชีพ (Open PVD)",
        nameEn: "Open Provident Fund",
        scheme: "cpamapp://open-pvd",
        url: buildUatLink(DESTINATIONS.OPEN_PVD),
        expected:
          "ยืนยัน session แล้วเข้า flow ลงทะเบียน PVD (ผู้ที่มี PVD อยู่แล้วจะข้ามไปหน้า Feed)",
      },
      {
        key: DESTINATIONS.SOFT_ONBOARD,
        nameTh: "สมัครสมาชิกใหม่ (Soft Onboarding)",
        nameEn: "Soft Onboarding",
        scheme: "cpamapp://soft-onboard",
        url: buildUatLink(DESTINATIONS.SOFT_ONBOARD),
        expected: "เปิดแอปเข้า flow Soft Onboarding สำหรับผู้ใช้ใหม่",
      },
      {
        key: DESTINATIONS.FORGOT_CREDENTIALS,
        nameTh: "ลืมชื่อผู้ใช้ / รหัสผ่าน (Forgot Credentials)",
        nameEn: "Forgot Username / Password",
        // The app registers BOTH paths, to different screens:
        //   forgot-credentials → PVDMigrateForgotUsernamePasswordIndex (PVD migrate)
        //   forgot-password    → ForgotPasswordSwitchAccount (Figma 7040:223679)
        // This journey is the second one, so the landing page maps
        // destination=forgot_credentials onto it (see SCHEME_PATH_OVERRIDES).
        scheme: "cpamapp://forgot-password",
        url: buildUatLink(DESTINATIONS.FORGOT_CREDENTIALS),
        expected: "เปิดแอปเข้า flow กู้คืนบัญชี (Account Recovery)",
      },
    ],
  },
];
